import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { Plus, PlayCircle, History, BarChart3, User, NotebookPen, Trophy, Pencil, Trash2, Users } from "lucide-react";
import { api } from "@/lib/handball";
import { SessionDialog } from "@/components/Dialogs";
import TournamentDialog from "@/components/TournamentDialog";
import ProfileShareDialog from "@/components/ProfileShareDialog";
import AppHeader from "@/components/AppHeader";
import TrendChart from "@/components/TrendChart";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/** Safely format a date string; returns "" if parsing fails (iOS Safari is strict). */
function safeFormatDate(value) {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    return format(d, "d MMM yyyy", { locale: sv });
  } catch {
    return "";
  }
}

export default function GoalkeeperMenu() {
  const { gkId } = useParams();
  const navigate = useNavigate();
  const [goalkeeper, setGoalkeeper] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessOpen, setSessOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [tournOpen, setTournOpen] = useState(false);
  const [tournEdit, setTournEdit] = useState(null); // tournament object being edited
  const [tournDelete, setTournDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadTournaments = async () => {
    try {
      const { data } = await api.get("/tournaments");
      setTournaments(data);
    } catch {
      setTournaments([]);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [gksRes, sessRes] = await Promise.all([
          api.get("/goalkeepers"),
          api.get("/sessions", { params: { goalkeeper_id: gkId, completed: false } }),
        ]);
        const gk = gksRes.data.find((g) => g.id === gkId);
        if (!gk) {
          toast.error("Målvakt hittades ej");
          navigate("/");
          return;
        }
        setGoalkeeper(gk);
        setSessions(sessRes.data);
        await loadTournaments();
      } finally {
        setLoading(false);
      }
    })();
  }, [gkId, navigate]);

  const createSession = async (payload) => {
    const { data } = await api.post("/sessions", payload);
    toast.success(`Session ${data.name} skapad`);
    navigate(`/gk/${gkId}/session/${data.id}/register`);
  };

  if (loading || !goalkeeper) {
    return (
      <div className="min-h-screen">
        <AppHeader subtitle="Meny" backTo="/profiles" backLabel="Profiler" />
        <main className="max-w-[1100px] mx-auto px-6 py-10 text-zinc-500 font-display uppercase tracking-widest">
          Laddar…
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <AppHeader subtitle={`Profil · ${goalkeeper.name}`} backTo="/profiles" backLabel="Profiler" />

      <main className="max-w-[1100px] mx-auto px-6 py-10 space-y-8">
        {/* Profile header block */}
        <section className="card-tactical p-6 flex items-center gap-4 flex-wrap">
          <div className="w-14 h-14 border-2 border-[#34C759] flex items-center justify-center bg-[#171717]">
            <User size={26} className="text-[#34C759]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display flex items-center gap-2 flex-wrap">
              Aktiv profil
              {goalkeeper.role === "editor" && (
                <span
                  data-testid="menu-role-badge"
                  className="text-[9px] font-bold tracking-widest border border-[#FFD60A] text-[#FFD60A] px-1.5 py-0.5"
                >
                  SKRIBENT · DELAD AV {goalkeeper.owner_name || "ÄGARE"}
                </span>
              )}
              {goalkeeper.role === "viewer" && (
                <span
                  data-testid="menu-role-badge"
                  className="text-[9px] font-bold tracking-widest border border-[#34C759] text-[#34C759] px-1.5 py-0.5"
                >
                  LÄSARE · DELAD AV {goalkeeper.owner_name || "ÄGARE"}
                </span>
              )}
            </div>
            <div
              className="font-display font-black uppercase tracking-widest text-3xl text-white leading-tight"
              data-testid="menu-goalkeeper-name"
            >
              {goalkeeper.name}
            </div>
            <div className="text-xs text-zinc-500 mt-1">
              {sessions.length} sparade sessioner
            </div>
          </div>
          {goalkeeper.role === "owner" && (
            <Button
              data-testid="menu-share-profile-btn"
              onClick={() => setShareDialogOpen(true)}
              variant="outline"
              className="h-10 rounded-none border-[#3F3F46] bg-transparent hover:bg-[#171717] text-white"
            >
              <Users size={14} className="mr-2" />
              Dela profil
            </Button>
          )}
        </section>

        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display mb-4">
            Vad vill du göra?
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {goalkeeper.role === "owner" && (
              <MenuCard
                testid="menu-new-tournament"
                icon={<Trophy size={22} className="text-[#FFD60A]" />}
                title="Skapa turnering/serie"
                subtitle="Samla matcher under en gemensam period – t.ex. seriespel eller cup."
                onClick={() => { setTournEdit(null); setTournOpen(true); }}
              />
            )}
            {goalkeeper.role !== "viewer" && (
              <MenuCard
                testid="menu-new-session"
                icon={<PlayCircle size={22} className="text-[#34C759]" />}
                title="Ny session"
                subtitle="Starta en ny match eller träning och börja registrera skott direkt."
                onClick={() => setSessOpen(true)}
              />
            )}

            {/* Continue-previous card: dropdown lives INSIDE the card so no scroll needed */}
            <div
              className="card-tactical p-5 h-full flex flex-col"
              data-testid="menu-continue-session"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 border border-[#3F3F46] flex items-center justify-center bg-[#171717]">
                  <History size={22} className="text-[#FFD60A]" />
                </div>
                <div className="font-display font-black uppercase tracking-widest text-lg text-white leading-tight">
                  Fortsätt tidigare session
                </div>
              </div>
              {sessions.length === 0 ? (
                <div
                  className="text-sm text-zinc-500 leading-relaxed"
                  data-testid="continue-empty"
                >
                  Inga aktiva sessioner. Skapa en ny session, eller återuppta en
                  avslutad match under "Utvärdera sessioner".
                </div>
              ) : (
                <div className="space-y-2 mt-auto">
                  <div className="text-sm text-zinc-400 mb-2">
                    Öppna en pågående session och registrera fler skott.
                  </div>
                  <Select
                    value=""
                    onValueChange={(sid) =>
                      navigate(`/gk/${gkId}/session/${sid}/register`)
                    }
                  >
                    <SelectTrigger
                      data-testid="continue-session-select"
                      className="h-11 bg-black border-[#3F3F46] text-white font-display uppercase tracking-wider rounded-none text-xs"
                    >
                      <SelectValue placeholder={`Välj bland ${sessions.length} aktiv${sessions.length === 1 ? "" : "a"} session${sessions.length === 1 ? "" : "er"}`} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-[#27272A] text-white max-h-[360px]">
                      {sessions.map((s) => (
                        <SelectItem
                          key={s.id}
                          value={s.id}
                          data-testid={`continue-session-option-${s.id}`}
                          className="py-2.5"
                        >
                          <div className="flex flex-col">
                            <span className="font-display uppercase tracking-widest text-sm">
                              {s.name}
                            </span>
                            <span className="text-[11px] text-zinc-500 mt-0.5">
                              {s.session_type === "match" ? "Match" : "Träning"}
                              {s.opponent ? ` · vs ${s.opponent}` : ""}
                              {safeFormatDate(s.date) ? ` · ${safeFormatDate(s.date)}` : ""}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <MenuCard
              testid="menu-evaluate"
              icon={<BarChart3 size={22} className="text-white" />}
              title="Utvärdera sessioner"
              subtitle="Analysera prestationen per match, tidsperiod eller all data."
              onClick={() => navigate(`/gk/${gkId}/evaluate`)}
            />
            <MenuCard
              testid="menu-journal"
              icon={<NotebookPen size={22} className="text-white" />}
              title="Dagbok"
              subtitle="Kommentarer, träningsupplägg och observationer för målvakten."
              onClick={() => navigate(`/gk/${gkId}/journal`)}
            />
          </div>
        </div>

        <TrendChart goalkeeperId={gkId} />

        {tournaments.length > 0 && goalkeeper.role === "owner" && (
          <section className="card-tactical p-5" data-testid="tournaments-list">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="section-hdr">
                <span className="marker" />
                Turneringar / serier
              </div>
              <Button
                data-testid="tournament-add-btn"
                onClick={() => { setTournEdit(null); setTournOpen(true); }}
                variant="outline"
                className="h-9 rounded-none border-[#3F3F46] bg-transparent hover:bg-[#171717] text-white text-xs"
              >
                <Plus size={14} className="mr-1.5" />
                Ny
              </Button>
            </div>
            <div className="space-y-2">
              {tournaments.map((t) => {
                const today = new Date().toISOString().slice(0, 10);
                const isActive = t.end_date >= today;
                return (
                  <div
                    key={t.id}
                    data-testid={`tournament-row-${t.id}`}
                    className="flex items-center gap-2 border border-[#27272A] hover:border-[#3F3F46] transition-colors p-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display uppercase tracking-widest text-white">
                          {t.name}
                        </span>
                        <span
                          className={`text-[9px] tracking-widest font-bold border px-1.5 py-0.5 ${
                            isActive
                              ? "border-[#34C759] text-[#34C759]"
                              : "border-zinc-600 text-zinc-500"
                          }`}
                          data-testid={`tournament-badge-${t.id}`}
                        >
                          {isActive ? "PÅGÅR" : "AVSLUTAD"}
                        </span>
                      </div>
                      <div className="text-xs text-zinc-500 mt-1">
                        {safeFormatDate(t.start_date + "T00:00:00") || t.start_date}
                        {" — "}
                        {safeFormatDate(t.end_date + "T00:00:00") || t.end_date}
                      </div>
                    </div>
                    <button
                      data-testid={`tournament-eval-${t.id}`}
                      onClick={() => navigate(`/gk/${gkId}/evaluate/tournament/${t.id}`)}
                      className="h-9 px-3 border border-[#27272A] hover:bg-[#171717] text-xs font-display uppercase tracking-widest text-white"
                      title="Visa statistik"
                    >
                      <BarChart3 size={14} />
                    </button>
                    <button
                      data-testid={`tournament-edit-${t.id}`}
                      onClick={() => { setTournEdit(t); setTournOpen(true); }}
                      className="h-9 px-3 border border-[#27272A] hover:bg-[#171717] text-xs text-zinc-400 hover:text-white"
                      title="Redigera"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      data-testid={`tournament-delete-${t.id}`}
                      onClick={() => setTournDelete(t)}
                      className="h-9 px-3 border border-[#27272A] text-[#FF3B30] hover:bg-[#FF3B30]/10"
                      title="Ta bort"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </main>

      <SessionDialog
        open={sessOpen}
        onOpenChange={setSessOpen}
        onCreate={createSession}
        goalkeeperId={gkId}
      />

      <TournamentDialog
        open={tournOpen}
        onOpenChange={setTournOpen}
        mode={tournEdit ? "edit" : "create"}
        tournament={tournEdit}
        onSaved={loadTournaments}
      />

      <ProfileShareDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        goalkeeperId={gkId}
        goalkeeperName={goalkeeper?.name}
      />

      <AlertDialog
        open={!!tournDelete}
        onOpenChange={(o) => !o && setTournDelete(null)}
      >
        <AlertDialogContent
          data-testid="tournament-delete-confirm"
          className="bg-[#111] border-[#27272A] text-white rounded-none"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-widest text-xl">
              Ta bort turnering?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              <span className="block text-white font-display tracking-wide mb-2">
                {tournDelete?.name}
              </span>
              Matcherna behålls men lämnar turneringen. Ingen skottdata försvinner.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel
              data-testid="tournament-delete-cancel"
              className="rounded-none border-[#3F3F46] bg-transparent hover:bg-[#171717] text-white font-display uppercase tracking-widest"
            >
              Avbryt
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="tournament-delete-confirm-btn"
              disabled={deleting}
              onClick={async () => {
                if (!tournDelete) return;
                setDeleting(true);
                try {
                  await api.delete(`/tournaments/${tournDelete.id}`);
                  toast.success("Turnering borttagen");
                  setTournDelete(null);
                  await loadTournaments();
                } catch {
                  toast.error("Kunde inte ta bort turneringen");
                } finally {
                  setDeleting(false);
                }
              }}
              className="rounded-none bg-[#FF3B30] hover:bg-[#e33228] text-white font-display uppercase tracking-widest"
            >
              {deleting ? "Tar bort…" : "Ja, ta bort"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function MenuCard({ icon, title, subtitle, onClick, testid, active }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`card-tactical p-5 text-left h-full flex flex-col transition-colors ${
        active ? "border-[#34C759]" : "hover:border-[#3F3F46]"
      }`}
      style={{ background: active ? "#0f1a12" : undefined }}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 border border-[#3F3F46] flex items-center justify-center bg-[#171717]">
          {icon}
        </div>
        <div className="font-display font-black uppercase tracking-widest text-lg text-white leading-tight">
          {title}
        </div>
      </div>
      <div className="text-sm text-zinc-400">{subtitle}</div>
    </button>
  );
}
